// Authorization layer (U3). The shared session says who (authentication). It
// deliberately says nothing about whether this principal may operate a partner
// organization (ESCALATIONS B3). That decision is partner-side.
//
// V1-CONTRACT.md section 5 rules that no `/v1` route serves this decision in
// v1.0: the partner backend answers it from its own organization membership,
// and a `/v1/partners/me` route may follow in v1.1. So this is built against a
// typed stub with exactly the interface that contract section records. Swapping
// in the real endpoint when it exists must touch only `fetchAuthorization`
// below and nothing else (spec 4.1).

import type { Principal } from '../session/session'

/** The org a principal may operate, and the roles they hold in it. */
export interface PartnerOrg {
  readonly id: string
  readonly name: string
  readonly roles: readonly string[]
}

/** The states from spec 4.2, plus `loading` for the async gap before an answer. */
export type PartnerAuthorization =
  | { readonly status: 'loading' }
  | { readonly status: 'authorized'; readonly org: PartnerOrg }
  | { readonly status: 'pending' }
  | { readonly status: 'none' }
  | { readonly status: 'error' }

/**
 * The seam the real partner backend slots into. Until then it fails closed to
 * `none`: an authenticated principal about whom nothing is known is not part of
 * a partner organization, which is exactly the `none` screen. It never returns
 * `authorized` without a real grant. Dev-only mocks (see `mock.ts`) inject the
 * other states for UX and tests; they are gated on `import.meta.env.DEV` and
 * cannot run in a production build.
 */
export async function fetchAuthorization(
  principal: Principal,
): Promise<PartnerAuthorization> {
  void principal
  return { status: 'none' }
}

/** The single predicate every guarded surface asks. */
export function isAuthorized(a: PartnerAuthorization): boolean {
  return a.status === 'authorized'
}
